import React, { useState } from "react";
import InputMask from "react-input-mask";
import {
  InputAdornment,
  IconButton,
  TextField as MUITextField,
} from "@mui/material";
import { useApp } from "context";
import { Constants, Helpers } from "utils";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

type Props = {
  sx?: any;
  id?: string;
  type?: string;
  name?: string;
  label?: string;
  clipboard?: boolean;
  placeholder?: string;
  value?: any;
  mask?: string;
  maskPlaceholder?: string;
  focused?: boolean;
  disabled?: boolean;
  required?: boolean;
  multiline?: boolean;
  autoHeight?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  spellCheck?: boolean;
  readOnly?: boolean;
  special?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
};

export const TextField = (props: Props) => {
  const { theme } = useApp();

  const [passwordSwitch, setPasswordSwitch] = useState(false);

  const isPassword = props.type === "password";
  const isClipboard = props.clipboard ?? false;
  const type = isPassword && !passwordSwitch ? "password" : "text";
  const backgroundColor = props.special
    ? theme.special
    : theme.palette.background.color;

  const handlePasswordSwitch = () =>
    setPasswordSwitch((prevPasswordSwitch) => !prevPasswordSwitch);

  const handleClipboard = () => navigator.clipboard.writeText(props.value);

  const renderTextField = () => {
    const conditionalProps = !props.mask
      ? {
          value: props.value,
          onBlur: props.onBlur,
          disabled: props.disabled,
          onChange: props.onChange,
        }
      : {};

    return (
      <MUITextField
        {...conditionalProps}
        variant="filled"
        type={type}
        id={props.id}
        autoComplete="off"
        name={props.name}
        label={props.label}
        autoFocus={props.focused}
        minRows={props.multiline ? Constants.TEXTAREA.MIN : undefined}
        maxRows={
          props.multiline
            ? props.autoHeight
              ? 1000
              : Constants.TEXTAREA.MAX
            : undefined
        }
        multiline={props.multiline}
        placeholder={props.placeholder}
        required={props.required ?? false}
        hiddenLabel={!Helpers.hasValue(props.label)}
        error={props.error}
        helperText={props.helperText}
        spellCheck={props.spellCheck}
        sx={{
          width: "100%",
          backgroundColor,
          "& label": {
            color:
              // props.readOnly
              //   ? theme.disabled.color
              //   :
              theme.palette.font.color,
            "&.Mui-disabled": {
              color: theme.color.disabled.color,
              WebkitTextFillColor: theme.color.disabled.color,
            },
          },
          // "& .Mui-readOnly": { color: theme.disabled.color },
          "& .MuiTextField-root": {
            backgroundColor,
            "&:hover": { backgroundColor },
          },
          "& .MuiInputBase-root": {
            paddingBottom: 0,
            backgroundColor,
            "&:hover": { backgroundColor },
          },
          "& .MuiFilledInput-root": {
            backgroundColor,
            "&:hover": { backgroundColor },
          },
          "& .MuiInputBase-input": {
            color: theme.palette.background.text,
            borderRadius: theme.border.radius / 4,
            // backgroundColor: theme.palette.background.color,
            borderBottomWidth: 2,
            borderBottomStyle: "solid",
            borderBottomColor: theme.palette.background.color,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            "&:focus": {
              borderBottomColor: theme.color.primary.color,
            },
          },
          "& .MuiInputBase-inputMultiline": {
            color: theme.palette.font.color,
          },
          "& input": {
            color: theme.palette.font.color,
            "&.Mui-disabled": {
              color: theme.color.disabled.accent,
              WebkitTextFillColor: theme.color.disabled.accent,
            },
          },
          ...props.sx,
        }}
        InputProps={{
          endAdornment:
            isPassword || isClipboard ? (
              <InputAdornment position="end">
                {isPassword ? (
                  <IconButton
                    edge="end"
                    tabIndex={-1}
                    onClick={handlePasswordSwitch}
                    sx={{ color: theme.palette.font.color }}
                  >
                    {passwordSwitch ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ) : isClipboard ? (
                  <IconButton
                    edge="end"
                    tabIndex={-1}
                    onClick={handleClipboard}
                    sx={{ color: theme.palette.font.color }}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                ) : null}
              </InputAdornment>
            ) : null,
          readOnly: props.readOnly,
        }}
      />
    );
  };

  return (
    <>
      {props.mask ? (
        <InputMask
          disabled={props.disabled}
          mask={props.mask}
          maskPlaceholder={props.maskPlaceholder}
          value={props.value}
          onBlur={props.onBlur}
          onChange={props.onChange}
        >
          {renderTextField()}
        </InputMask>
      ) : (
        renderTextField()
      )}
    </>
  );
};
